/**
 * SP mesajı (RAISERROR / ProcessResult / onay metni) — güvenli HTML alt kümesiyle (issue #446).
 *
 * Blazor bu mesajları HTML olarak basıyordu; SP yazarları kalın başlık, satır sonu, renkli
 * uyarı ve madde listesi için etiket kullanıyor. React tarafı etiketleri siliyordu, mesaj
 * tek satır düz yazıya dönüşüyordu. Mesaj kullanıcı verisi de taşıyabildiği için HTML
 * olduğu gibi basılmıyor: bkz. `sanitizeSpHtml`.
 */
import { useMemo } from 'react';
import { cn } from '@/shared/lib/cn';
import { cleanSpMessage, hasSpHtml, sanitizeSpHtml } from '@/shared/lib/octo/spMessage';

export function SpMessage({ text, className }: { text: string; className?: string }) {
  const html = useMemo(() => (hasSpHtml(text) ? sanitizeSpHtml(text) : null), [text]);
  if (html === null) {
    // Düz metin: satır sonları korunur (Konum/Satır bloğu, çok satırlı açıklamalar).
    return <div className={cn('break-words whitespace-pre-line', className)}>{cleanSpMessage(text)}</div>;
  }
  return (
    <div
      className={cn(
        'break-words',
        // Tailwind preflight liste işaretlerini ve boşlukları sıfırlıyor; SP'nin yazdığı
        // listeler madde işaretiyle görünsün.
        '[&_ul]:list-disc [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:pl-5 [&_li]:my-0.5 [&_p]:my-1 [&_ol]:my-1 [&_ul]:my-1',
        '[&_b]:font-semibold [&_strong]:font-semibold [&_a]:underline',
        className,
      )}
      // sanitizeSpHtml'den geçti: yalnızca biçim etiketleri ve süzülmüş stil kalır.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** `cleanSpMessage(x) || yedek` kalıbının zengin karşılığı: mesaj boşsa yedek metin. */
export function spMessageNode(text: string | null | undefined, fallback?: string) {
  const value = (text ?? '').trim();
  if (!value) return fallback ?? '';
  // Tek satırlık düz metin olduğu gibi kalır; biçim ya da satır sonu varsa bileşen gerekir.
  if (!hasSpHtml(value) && !/\n|\[br\s*\/?\]|\\r?\\n/i.test(value)) return cleanSpMessage(value);
  return <SpMessage text={value} />;
}
